import React, { useEffect, useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

import {
  HubCancelSubmitGroup,
  HubDialogContent,
  HubHookFormWrapper,
} from "../../components";
import {
  useGetFinancialPeriodDetails,
  usePostFinancialPeriod,
  usePutFinancialPeriod,
} from "../../hooks/react-query/useFinancialsData";
import { getUserLocation } from "../../redux/auth";
import { useDialogContext } from "../HubDialogWrapper";

import FinancialPeriodForm from "./FinancialPeriodForm";

const toIso = (val) => (val ? dayjs(val).toISOString() : null);

const FinancialPeriodContent = () => {
  const { onClose, open } = useDialogContext();
  const defaultLocation = useSelector(getUserLocation);

  // open === true => create; open === <id> => edit
  const isEdit = typeof open === "number" || (open && open !== true);
  const id = isEdit ? Number(open) : null;

  const { data: existing, isLoading: isLoadingDetails } =
    useGetFinancialPeriodDetails(id);

  const {
    mutate: createPeriod,
    isLoading: isCreating,
    isSuccess: isCreated,
  } = usePostFinancialPeriod();
  const {
    mutate: updatePeriod,
    isLoading: isUpdating,
    isSuccess: isUpdated,
  } = usePutFinancialPeriod();

  useEffect(() => {
    if (isCreated || isUpdated) onClose();
  }, [isCreated, isUpdated]);

  const defaultValues = useMemo(() => {
    if (!isEdit) {
      return {
        locationId: defaultLocation,
        startDate: dayjs().toISOString(),
      };
    }
    if (!existing) return undefined;
    return {
      ...existing,
      startDate: existing?.startDate || null,
      endDate: existing?.endDate || null,
    };
  }, [isEdit, existing, defaultLocation]);

  const onSubmit = (formData) => {
    const payload = {
      name: formData.name,
      locationId: Number(formData.locationId),
      startDate: toIso(formData.startDate),
      endDate: toIso(formData.endDate),
      historicalTreeCost: Number(formData.historicalTreeCost) || 0,
      historicalMaterialCost: Number(formData.historicalMaterialCost) || 0,
      historicalFactor: Number(formData.historicalFactor) || 0,
    };

    if (isEdit) updatePeriod({ id, ...payload });
    else createPeriod(payload);
  };

  const submitting = isCreating || isUpdating;

  return (
    <HubDialogContent largePadding>
      <HubHookFormWrapper defaultValues={defaultValues} onSubmit={onSubmit}>
        <Stack gap="25px" alignItems="center">
          <Stack alignItems="center">
            <Typography variant="h6">
              {isEdit ? "Edit Financial Period" : "Create Financial Period"}
            </Typography>
            <Typography variant="caption">
              Period values are used in quote COGS calculations
            </Typography>
          </Stack>
          <FinancialPeriodForm
            defaultLocation={defaultLocation}
            isEdit={isEdit}
          />
          <HubCancelSubmitGroup
            disabled={submitting || (isEdit && isLoadingDetails)}
            isLoading={submitting}
            label={isEdit ? "Save Changes" : "Create Period"}
          />
        </Stack>
      </HubHookFormWrapper>
    </HubDialogContent>
  );
};

export default FinancialPeriodContent;
